// In sms/live-preview.tsx - Update the component
import { ChevronDown, ChevronUp } from "lucide-react";
import { LivePreviewPanelProps } from "./types";
import { PhoneMockup } from "../phone-mock";
import { CostEstimateResponse } from "./types";
import { WhatsappMockUp } from "../whatsapp-mock";
import { CTAButton, TemplateVariables } from "../../../../views/marketing-pages/create-campaign/types";
import { EmailMockUp } from "../email-mockup";
import { RcsPreview } from "../../templates/rcs/rcs-preview";
import { RcsComponent } from "../../templates/rcs/rcs-types";

interface ExtendedLivePreviewPanelProps extends LivePreviewPanelProps {
  costEstimate?: CostEstimateResponse | null;
  smsSegments?: number;
  channel?: string;
  mediaFile?: File | null | undefined;
  mediaUrl?: string;
  // Add these props
  vars?: TemplateVariables;
  cta?: CTAButton;
  // The template's own full buttons array (e.g. 2-3 quick replies) — `cta`
  // alone only ever carries a single overridden CTA button, so without this
  // the preview here silently dropped every button but the first.
  templateButtons?: any[];
  templateName?: string;
  rcsComponents?: RcsComponent[];
  // The template's own declared header — needed so an image/video/document/
  // text header still shows here even when the marketer hasn't picked a
  // fresh file for this particular send (mediaUrl/mediaFile cover that case).
  headerType?: string | null;
  headerContent?: string | null;
}

export const LivePreviewPanel: React.FC<ExtendedLivePreviewPanelProps> = ({
  isExpanded,
  onToggle,
  senderName,
  messageText,
  costEstimate,
  smsSegments,
  channel,
  mediaFile,
  mediaUrl,
  vars,
  cta,
  templateButtons,
  subject,
  previewText,
  htmlContent,
  rcsComponents,
  headerType,
  headerContent,
}) => {

  // Calculate values from costEstimate
  const audienceCount = costEstimate?.campaign_size || 0;
  const totalAudience = costEstimate?.total_contacts || 0;
  const costPerMessage = costEstimate?.cost_per_unit || 0;
  const totalCost = costEstimate?.estimated_cost || 0;
  const currencySymbol = costEstimate?.currency_symbol;

  const audiencePercent = totalAudience > 0 ? Math.round((audienceCount / totalAudience) * 100) : 0;

  // "0 of N contacts" reads as a bug/error to a marketer. It's usually just
  // that their audience doesn't include anyone in their own org's default
  // country — surface that explicitly instead of leaving them staring at a
  // bare zero.
  const getUserCountryName = (): string | null => {
    try {
      const raw =
        localStorage.getItem("userDetails") ||
        localStorage.getItem("user") ||
        localStorage.getItem("user_data");
      const user = raw ? JSON.parse(raw) : null;
      return user?.country_name || user?.businessLocation || user?.country || null;
    } catch {
      return null;
    }
  };
  const userCountryName = audienceCount === 0 && totalAudience > 0 ? getUserCountryName() : null;
  return (
    <div className="bg-white dark:bg-gray-900 w-full border rounded-xl border-gray-200 dark:border-gray-700 h-full flex flex-col">

      {/* Estimated Reach */}
      <div className="p-4 border-gray-100 dark:border-gray-800">
        <button
          onClick={onToggle}
          className="w-full flex items-center justify-between"
        >
          <div className="flex items-center gap-2">
            <span className="font-medium text-gray-900 dark:text-gray-100 text-sm">Your Estimated Reach</span>
            <span className="w-3 h-3 bg-green-500 rounded-full block" />
          </div>
          {isExpanded ? (
            <ChevronUp className="w-4 h-4 text-[#7B7B7B] font-semibold dark:text-gray-500" />
          ) : (
            <ChevronDown className="w-4 h-4 text-[#7B7B7B] font-semibold dark:text-gray-500" />
          )}
        </button>

        {isExpanded && (
          <div className="mt-3 space-y-3">
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Audience Selected</p>
              <p className="text-sm font-semibold text-indigo-600 dark:text-indigo-400">
                {audienceCount.toLocaleString()} of {totalAudience.toLocaleString()} contacts
              </p>
              {audienceCount === 0 && totalAudience > 0 && (
                <p className="mt-0.5 text-[11px] bg-[#FAD4D1] rounded-md p-2 text-[#3A3428]">
                  <span className="font-semibold text-[#3A3428] text-[14px] block">No contacts match</span>
                  None of the uploaded numbers match your default country  {userCountryName ? `your default country (${userCountryName})` : "your default country"}. Upload contacts with matching country codes to continue.
                </p>
              )}
              <div className="mt-1 h-1.5 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                <div
                  className="h-full bg-indigo-500 rounded-full transition-all"
                  style={{ width: `${audiencePercent}%` }}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {channel === 'sms' && (
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">SMS Parts</p>
                  <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{smsSegments}</p>
                </div>
              )}

              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">Cost per message</p>
                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {currencySymbol} {costPerMessage.toFixed(2)}
                </p>
              </div>
            </div>

            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg py-3 px-1">
              <p className="text-xs text-gray-500 dark:text-gray-400">Total cost</p>
              <p className="text-lg font-bold text-gray-900 dark:text-gray-100">
                {currencySymbol} {totalCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Phone Preview */}
      <div className="flex-1 p-4 overflow-y-auto">
        {channel === 'whatsapp' ? (
          <WhatsappMockUp
            message={messageText}
            mediaUrl={mediaUrl}
            mediaFile={mediaFile}
            vars={vars}
            cta={cta}
            template={{ buttons: templateButtons, header_type: headerType, header_content: headerContent }}
            senderName={senderName}
          />
        ) : channel === 'email' ? (
          <EmailMockUp
            subject={subject}
            previewText={previewText}
            senderName={senderName}
            htmlContent={htmlContent}
            plainText={messageText}
          />
        ) : channel === 'rcs' ? (
          <RcsPreview
            components={rcsComponents || []}
            senderName={senderName}
          />
        ) : (
          <PhoneMockup message={messageText} senderName={senderName} />
        )}
      </div>
    </div>
  );
};